import { RefreshButton } from '@bumble/app/components/RefreshButton';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import {
  Box,
  Button,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem
} from '@mui/material';
import { GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';
import React, { ReactElement, useEffect, useState } from 'react';

interface TableActionsProps {
  itemsSelected?: boolean;
  btnLabel?: string | null;
  btnOnclick?: () => void | null;
  btnDisabled?: boolean;
  extraButtonOnClick?: () => void;
  extraButtonLabel?: string;
  extraButtonDisabled?: boolean;
  refreshData?: () => void | null;
  isRefetching?: boolean;
  refreshDisabled?: boolean;
  menuItems?:
    | {
        label: string;
        icon: React.ReactNode;
        onClick: () => void;
        hasDivider?: boolean;
        disabled?: boolean;
      }[]
    | null;
  secondarySlot?: React.ReactNode;
  hideSearchBar?: boolean;
}

function TableActions({
  itemsSelected,
  btnLabel = '',
  btnOnclick,
  btnDisabled = false,
  extraButtonLabel = '',
  extraButtonOnClick,
  extraButtonDisabled = false,
  refreshData,
  isRefetching,
  refreshDisabled = false,
  menuItems,
  secondarySlot,
  hideSearchBar = false,
}: TableActionsProps) {
  const [actionsEl, setActionsEl] = useState<null | HTMLElement>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const openActionsMenu = Boolean(actionsEl);
  const handleActionsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setActionsEl(event.currentTarget);
  };
  const handleActionsClose = () => {
    setActionsEl(null);
  };
  const [rotateIcon, setRotateIcon] = useState(0);

  const handleRefreshClick = async () => {
    setRotateIcon(rotateIcon + 1);
    setIsRefreshing(true);
    if (refreshData && !isRefreshing) refreshData();
  };

  useEffect(() => {
    if (!isRefetching) setIsRefreshing(false);
  }, [isRefetching]);

  const menuItemsArray: ReactElement[] = [];
  menuItems?.forEach(({ label, icon, onClick, hasDivider, disabled }, index) => {
    if (hasDivider && index > 0) {
      menuItemsArray.push(<Divider key={`divider-${label}`} />);
    }
    menuItemsArray.push(
      <MenuItem
        onClick={() => {
          onClick();
          handleActionsClose();
        }}
        key={label}
        title={label}
        disabled={disabled}
      >
        <ListItemIcon>{icon}</ListItemIcon>
        <ListItemText>{label}</ListItemText>
      </MenuItem>,
    );
  });

  return (
    <GridToolbarContainer sx={{ mb: 2 }}>
      {hideSearchBar ? null : <GridToolbarQuickFilter
        variant="outlined"
        size="small"
        debounceMs={250}
        sx={{ mr: 'auto' }}
      />}
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        mb={2}
        gap={2}
        sx={{
          justifyContent: 'flex-end',
          ml: 'auto'
        }}
      >
        {extraButtonLabel && (
          <Button
            size="medium"
            variant="contained"
            color="primary"
            onClick={extraButtonOnClick}
            disabled={extraButtonDisabled}
            title={extraButtonLabel}
          >
            <Box component="span" sx={{ display: { md: 'inline', xs: 'none' } }}>
              {extraButtonLabel}
            </Box>
            <Box component="span" sx={{ display: { md: 'none', xs: 'inline' } }}>
              ExtraButton
            </Box>
          </Button>
        )}
        {btnLabel && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<AddOutlinedIcon fontSize="small" sx={{ marginRight: { md: 1, xs: 0 } }} />}
            onClick={btnOnclick}
            disabled={btnDisabled}
            title="New"
          >
            <Box component="span" sx={{ display: { md: 'inline', xs: 'none' } }}>
              {btnLabel}
            </Box>
            <Box component="span" sx={{ display: { md: 'none', xs: 'inline' } }}>
              New
            </Box>
          </Button>
        )}
        {refreshData && (
          <RefreshButton onRefresh={handleRefreshClick} disable={refreshDisabled || !!isRefreshing} />
        )}
        {menuItems && (
          <>
            <Button
              size="small"
              variant="outlined"
              endIcon={<KeyboardArrowDownOutlinedIcon fontSize="small" />}
              id="actions-button"
              aria-label="menu"
              aria-controls={openActionsMenu ? 'actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={openActionsMenu ? 'true' : undefined}
              onClick={handleActionsClick}
              disabled={!itemsSelected}
              title="Actions"
            >
              Actions
            </Button>
            <Menu
              elevation={1}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              id="actions-menu"
              anchorEl={actionsEl}
              open={openActionsMenu}
              onClose={handleActionsClose}
              MenuListProps={{
                'aria-labelledby': 'actions-button',
              }}
            >
              {menuItemsArray}
            </Menu>
          </>
        )}
        {secondarySlot}
      </Box>
    </GridToolbarContainer>
  );
}

export default TableActions;
